import { classifyOrderSetFile } from "./classify";
import type {
  ClassifiedOrderSetFile,
  OrderSetCandidate,
  OrderSetGrouping,
  OrderSetIssue,
  OrderSetFileInput,
  OrderSetIssueCode,
  OrderSetLanguageLayer,
} from "./types";

function byName(
  left: ClassifiedOrderSetFile,
  right: ClassifiedOrderSetFile,
): number {
  return left.name.localeCompare(right.name);
}

/**
 * Picks the single file holding a role. Two candidates for one role is a real
 * ambiguity in the supplied set, so the slot stays empty and the names are
 * reported rather than one being chosen.
 */
function soleFile(
  files: readonly ClassifiedOrderSetFile[],
  setKey: string,
  issues: OrderSetIssue[],
): ClassifiedOrderSetFile | null {
  const [first] = files;
  if (first === undefined) return null;
  if (files.length > 1) {
    issues.push({
      code: "AMBIGUOUS_ROLE",
      fileNames: files.map(({ name }) => name),
      setKey,
    });
    return null;
  }
  return first;
}

function issue(
  code: OrderSetIssueCode,
  files: readonly ClassifiedOrderSetFile[],
  setKey: string | null,
): OrderSetIssue {
  return { code, fileNames: files.map(({ name }) => name), setKey };
}

/**
 * Associates supplied files into order sets by set identity and language
 * layer. Selection order never affects the result.
 *
 * A document layer is its own set: doc 03 and the 11 September 2026 handover
 * record that the English and French layers of one source can describe
 * different scenarios, so a French document never completes an English set.
 * A photographed trace carries no language and is offered to every layer of
 * its set.
 */
export function groupOrderSetFiles(
  supplied: readonly (OrderSetFileInput | string)[],
): OrderSetGrouping {
  const classified = supplied
    .map((one) => classifyOrderSetFile(one))
    .sort(byName);
  const issues: OrderSetIssue[] = [];

  const unsupported = classified.filter(
    ({ format }) => format === "unsupported",
  );
  if (unsupported.length > 0)
    issues.push(issue("UNSUPPORTED_FORMAT", unsupported, null));

  const unassigned = classified.filter(({ setId }) => setId === null);
  if (unassigned.length > 0)
    issues.push(issue("NO_SET_IDENTITY", unassigned, null));

  const identified = classified.filter(
    (file): file is ClassifiedOrderSetFile & { setId: string } =>
      file.setId !== null,
  );
  const unknownRole = identified.filter(({ role }) => role === "unknown");
  if (unknownRole.length > 0)
    issues.push(issue("UNKNOWN_ROLE", unknownRole, null));

  // A file whose role could not be established is reported, never guessed
  // into a set.
  const usable = identified.filter(({ role }) => role !== "unknown");
  const setIds = [...new Set(usable.map(({ setId }) => setId))].sort();
  const sets: OrderSetCandidate[] = [];

  for (const setId of setIds) {
    const inSet = usable.filter((file) => file.setId === setId);
    const traces = inSet.filter(({ role }) => role === "trace-image");
    const layers = [
      ...new Set(
        inSet
          .filter(({ role }) => role !== "trace-image")
          .map(({ languageLayer }) => languageLayer),
      ),
    ].sort();
    const presentLayers: readonly OrderSetLanguageLayer[] =
      layers.length === 0 ? ["unmarked"] : layers;

    for (const languageLayer of presentLayers) {
      const setKey = `${setId}:${languageLayer}`;
      // Only documents of this exact layer. An unmarked document is not
      // evidence for either language layer and never completes one.
      const documents = inSet.filter(
        (file) =>
          file.role !== "trace-image" && file.languageLayer === languageLayer,
      );
      const warningOrder = soleFile(
        documents.filter(({ role }) => role === "warning-order"),
        setKey,
        issues,
      );
      const ordersCard = soleFile(
        documents.filter(({ role }) => role === "orders-card"),
        setKey,
        issues,
      );
      const traceImage = soleFile(traces, setKey, issues);

      if (warningOrder === null)
        issues.push(issue("MISSING_WARNING_ORDER", [], setKey));
      if (traceImage === null && traces.length === 0)
        issues.push(issue("MISSING_TRACE_IMAGE", [], setKey));

      sets.push({
        files: [...documents, ...traces].sort(byName),
        languageLayer,
        ordersCard,
        setId,
        setKey,
        traceImage,
        warningOrder,
      });
    }
  }

  return { issues, sets, unassigned };
}
